import { useEffect, useRef, useState } from "react";
import {
  Button,
  EmailWithValidation,
  PostalCodeWithValidation,
  SlideOutModal,
  SlideOutModalProps,
} from "../../../components";
import { Button as BSButton} from 'react-bootstrap'
import { Formik } from "formik";
import { Form, Row, Col, FloatingLabel } from "react-bootstrap";
import { sharedProperty, useAsync, useUrlParams } from "../../../hooks";
import { 
    CardElement, 
    useStripe, 
    useElements, 
} from "@stripe/react-stripe-js";
import * as yup from "yup";
import { PaymentRepo } from "../../../data/repo";
import { PhoneNumberRegex } from "../../../app";
import { OrganizationService, SubscriptionService } from "../../../services";

export interface AddPaymentModalProps extends SlideOutModalProps {
  showState: sharedProperty<boolean>;
  onSubmit: (paymentMethodId: string) => void;
}

export function AddPaymentInformationModal(props: Readonly<AddPaymentModalProps>) {
  const submitRef = useRef<HTMLButtonElement>(null);
  const stripe = useStripe();
  const elements = useElements();
  const subscriptionId = parseInt(useUrlParams("id"));

  const subscriptionService =new SubscriptionService();
  const {value: subscription} = useAsync(() => subscriptionService.get(subscriptionId), [subscriptionId]);

  const organizationService = new OrganizationService();
  const organizationId = subscription?.organizationId!;
  const {value: organization} = useAsync(() => organizationService.get(organizationId), [organizationId]);

  const [loading, setLoading] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    email: "",
    phone: "",
    postalCode: ""
  });

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (organization?.billingEmail) {
        const details = await paymentRepo.GetCustomerDetailsAsync(organization?.billingEmail);
        setCustomerDetails(details);
      }
    };
    fetchCustomerDetails();
  }, [organization]);

  const paymentRepo = new PaymentRepo();

  const validationSchema = yup.object().shape({
    phoneNumber: yup.string()
      .matches(PhoneNumberRegex, "Phone number is invalid")
      .required("Phone number is required"),
  });

  const handleSubmitPayment = async (values: any) => {
    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
        console.log("CardElement not found");
        return;
    }

    setLoading(true);

    try {
      const { token, error } = await stripe.createToken(cardElement);

      if (error) {
        console.error("Error creating token:", error.message);
        setLoading(false);
        return;
      }

      const response = await paymentRepo.HandlePaymentRequestAsync({
        organizationId: organization?.id!,
        subscriptionId: subscriptionId,
        token: token.id,
        email: values.email,
        phoneNumber: values.phoneNumber,
        postalCode: values.postalCode,
      });

      if (response.success) {
        props.onSubmit(response.paymentMethodId);
        props.showState.setter(false);
      } else {
        console.error("Failed to create customer and save payment method.");
      }
    } catch (error) {
      console.error("Error processing payment:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlideOutModal
      {...props}
      title="Add Payment Information"
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button onClick={() => props.showState.setter(false)} variant="cancel" className="w-100">
                Cancel
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button onClick={() => submitRef.current?.click()} variant="primary" className="w-100" showSpinner={loading ?? true}>
                Save
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={{
            postalCode: customerDetails.postalCode,
            phoneNumber: customerDetails.phone || "",
            email: organization?.billingEmail || "",
        }}
        onSubmit={handleSubmitPayment}
      >
        {({ values, handleSubmit, touched, errors, handleChange }) => {
          return (
            <Form onSubmit={handleSubmit}>
             <Row>
              <Col>
                <EmailWithValidation 
                 id={"PaymentInformationEmail"} 
                 name="email"
                 initialValue={organization?.billingEmail || "" } />
              </Col>
             </Row>
             <Row>
                <Col className="mb-3">
                  <div style={{ border: "1px solid #ccc", padding: "1rem 0.75rem", borderRadius: "20px" }}>
                    <CardElement options={{hidePostalCode: true}} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <PostalCodeWithValidation 
                   name="postalCode"
                   label="Postal Code"/>
                </Col>
                <Col className="mb-3">
                  <FloatingLabel label="Phone Number" className="mb-3">
                    <Form.Control
                      type="text"
                      name="phoneNumber"
                      placeholder="xxx-xxx-xxxx"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      isInvalid={touched.phoneNumber && !!errors.phoneNumber}
                    />
                    <Form.Control.Feedback type="invalid">
                    {touched.phoneNumber && errors.phoneNumber ? errors.phoneNumber : ''}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
              </Row>

            <BSButton ref={submitRef} type="submit" size="lg" className="d-none" variant="primary">Save</BSButton>
          </Form>
          );
        }}
      </Formik>
    </SlideOutModal>
  );
}

export default AddPaymentInformationModal;
